import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'

import axios from '../../dataClient/dataClient'
import Button, { SecondaryButton } from '../common/Button'
import { ErrorMessage } from '../common/UserMessage'
import { FlexDialogContent, FlexEndContainer, StyledDialog } from '../styles/Containers'
import BodyText, { BoldFormTitle } from '../styles/Text'

const emptyPayload = {
  name: '',
  body: ''
}

function SaveTemplateButton({ payload = emptyPayload, templateId, open = false, disabled = false, onClose = () => {}, onSuccess = () => {} }) {
  const isSavingRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const persistedTemplateId = String(templateId ?? '').trim()
  const isCreatingTemplate = persistedTemplateId === ''
  const confirmationTitle = isCreatingTemplate ? 'Create New Letter Template' : 'Save Letter Template'
  const confirmationText = isCreatingTemplate
    ? 'Creating this template will add it for all users.'
    : 'Saving this template will change the template for all users.'
  const errorText = isCreatingTemplate
    ? 'There was an error creating the template. Please try again.'
    : 'There was an error saving the template. Please try again.'
  const confirmationButtonText = isCreatingTemplate ? 'Create' : 'Save'

  useEffect(() => {
    if (!open) return

    setError(false)
    setIsLoading(false)
  }, [open])

  function handleSave() {
    if (isSavingRef.current) return

    isSavingRef.current = true
    setError(false)
    setIsLoading(true)

    const request = isCreatingTemplate
      ? axios.post('/letter/letter-template', payload)
      : axios.patch(`/letter/letter-template/${persistedTemplateId}`, payload)

    request
      .then(({ data }) => {
        onSuccess(data.data)
        onClose()
      })
      .catch(e => {
        console.error('Failed to save the letter template', e)
        isSavingRef.current = false
        setError(true)
      })
      .finally(() => {
        isSavingRef.current = false
        setIsLoading(false)
      })
  }

  return (
    <>
      <Button type="submit" disabled={disabled}>
        Save
      </Button>
      <StyledDialog fullWidth open={open} onClose={onClose} keepMounted={false} aria-labelledby="save-template-title">
        <FlexDialogContent>
          <BoldFormTitle id="save-template-title">
            {confirmationTitle}: {payload.name}
          </BoldFormTitle>
          <BodyText>{confirmationText}</BodyText>
          {error && <ErrorMessage>{errorText}</ErrorMessage>}
          <FlexEndContainer>
            <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </SecondaryButton>
            <Button type="button" onClick={handleSave} loading={isLoading}>
              {confirmationButtonText}
            </Button>
          </FlexEndContainer>
        </FlexDialogContent>
      </StyledDialog>
    </>
  )
}

SaveTemplateButton.propTypes = {
  payload: PropTypes.shape({
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired
  }),
  templateId: PropTypes.string,
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}

export default SaveTemplateButton
